import type { CincInspectionResponse } from '../api/cinc';

export const CINC_INSPECTION_ORG = '70299cd6-a817-4eeb-a333-df660b1f204e';

export interface CincInspectionScope {
  environment: string | undefined;
  isSuperAdmin: boolean;
  selectedOrgId: string | undefined;
  detailOrgId: string | undefined;
}

export const canInspectCinc = (scope: CincInspectionScope): boolean =>
  scope.environment === 'uat' &&
  scope.isSuperAdmin &&
  scope.selectedOrgId === CINC_INSPECTION_ORG &&
  scope.detailOrgId === scope.selectedOrgId;

const safeProfileSegment = /^[A-Za-z][A-Za-z0-9_]{0,49}$/;
const secretSegment =
  /password|token|secret|credential|key|auth|data|email|phone|address|label|avatar|pin|otp|passcode|verification|recovery|mfa|code/i;
const profileTypes = new Set(['object', 'array', 'boolean', 'number', 'string', 'null', 'other']);
const authStatuses = new Set(['missing', 'matches_api_key', 'other_recognized', 'unrecognized']);

export interface SafeCincInspection {
  status: 'not_qualified';
  reason: 'provider_account_field_unreviewed' | 'credential_metadata_unavailable';
  existingBinding: boolean;
  readOnly: boolean;
  writes: 0;
  metadata: {
    connectorFound: boolean;
    connectorAuthType: 'api_key' | 'oauth2' | null;
    connectorAuthStatus: string;
    credentialMatchCount: 0 | 1 | 2;
    credentialValid: boolean;
    profilePaths: { path: string; type: string }[];
  };
}

/** Copy only the API's documented display fields; never render arbitrary response content. */
export const safeCincInspection = (raw: CincInspectionResponse): SafeCincInspection | null => {
  if (
    raw?.status !== 'not_qualified' ||
    !['provider_account_field_unreviewed', 'credential_metadata_unavailable'].includes(
      raw.reason
    ) ||
    raw.existing_binding !== true ||
    raw.read_only !== true ||
    raw.writes !== 0 ||
    !raw.metadata ||
    typeof raw.metadata.connector_found !== 'boolean' ||
    typeof raw.metadata.credential_valid !== 'boolean' ||
    !authStatuses.has(raw.metadata.connector_auth_status) ||
    ![0, 1, 2].includes(raw.metadata.credential_match_count)
  )
    return null;

  const authType = raw.metadata.connector_auth_type;
  if (authType !== null && authType !== 'api_key' && authType !== 'oauth2') return null;

  const profilePaths = Array.isArray(raw.metadata.profile_paths)
    ? raw.metadata.profile_paths
        .slice(0, 32)
        .filter((item) => {
          if (!item || typeof item.path !== 'string' || typeof item.type !== 'string') return false;
          const segments = item.path.split('.');
          return (
            segments.length <= 2 &&
            segments.every((part) => safeProfileSegment.test(part) && !secretSegment.test(part)) &&
            profileTypes.has(item.type)
          );
        })
        .map(({ path, type }) => ({ path, type }))
    : [];
  return {
    status: 'not_qualified',
    reason: raw.reason,
    existingBinding: true,
    readOnly: true,
    writes: 0,
    metadata: {
      connectorFound: raw.metadata.connector_found === true,
      connectorAuthType: authType,
      connectorAuthStatus: raw.metadata.connector_auth_status,
      credentialMatchCount: raw.metadata.credential_match_count as 0 | 1 | 2,
      credentialValid: raw.metadata.credential_valid === true,
      profilePaths,
    },
  };
};

export type CincInspectionView = {
  key: string;
  version: number;
  phase: 'idle' | 'loading' | 'result' | 'error';
  result?: SafeCincInspection;
  error?: 'Inspection unavailable. No qualification was performed.';
};

const scopeKey = (scope: CincInspectionScope): string =>
  [scope.environment, scope.isSuperAdmin, scope.selectedOrgId, scope.detailOrgId].join('|');

/** Single-flight manual request, invalidated on every selection or role change. */
export const createCincInspectionSession = (
  inspect: () => Promise<CincInspectionResponse>,
  publish: (view: CincInspectionView) => void
) => {
  let key = '';
  let version = 0;
  let pendingVersion: number | null = null;
  let view: CincInspectionView = { key, version, phase: 'idle' };

  const observe = (scope: CincInspectionScope): CincInspectionView => {
    const nextKey = scopeKey(scope);
    if (nextKey !== key) {
      key = nextKey;
      version += 1;
      pendingVersion = null;
      view = { key, version, phase: 'idle' };
    }
    return view;
  };

  const run = async (scope: CincInspectionScope): Promise<void> => {
    observe(scope);
    if (!canInspectCinc(scope) || pendingVersion === version) return;
    const requestVersion = version;
    pendingVersion = requestVersion;
    view = { key, version, phase: 'loading' };
    publish(view);
    try {
      const result = safeCincInspection(await inspect());
      if (version !== requestVersion) return;
      view = result
        ? { key, version, phase: 'result', result }
        : {
            key,
            version,
            phase: 'error',
            error: 'Inspection unavailable. No qualification was performed.',
          };
      publish(view);
    } catch {
      if (version !== requestVersion) return;
      view = {
        key,
        version,
        phase: 'error',
        error: 'Inspection unavailable. No qualification was performed.',
      };
      publish(view);
    } finally {
      if (pendingVersion === requestVersion) pendingVersion = null;
    }
  };
  return { observe, run };
};
